// Maps a ThemeDef preset to the `--mk-*` CSS custom properties that the
// marketing surface (MarketingShell + all marketing pages) reads through
// MARKETING_TOKENS. Setting these vars on any ancestor element re-skins the
// whole marketing UI live — available for any future "pick your brand
// colour" feature.
//
// The marketing tokens fall back to the committed default theme when these
// vars are unset, so pages that don't set them render exactly as before.

import type { ThemeDef } from '@/constants/themes';

/** "#B85C38" -> "184,92,56" (for use inside rgba(var(--x), a)). */
function hexToRgb(hex: string): string {
  const h = hex.replace('#', '').trim();
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}

/** Linear blend of two hex colours; t=0 → a, t=1 → b. Returns #rrggbb. */
function mix(a: string, b: string, t: number): string {
  const pa = hexToRgb(a).split(',').map(Number);
  const pb = hexToRgb(b).split(',').map(Number);
  const c = pa.map((v, i) => Math.round(v + (pb[i] - v) * t));
  return `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/** CSS custom-property map for a theme, spreadable into a React `style` prop. */
export function marketingVars(theme: ThemeDef): React.CSSProperties {
  return {
    '--mk-bg': theme.bg,
    '--mk-surface': theme.surface,
    '--mk-surface-alt': theme.surfaceAlt,
    '--mk-border': theme.border,
    '--mk-text': theme.text,
    '--mk-muted': theme.muted,
    '--mk-primary': theme.primary,
    '--mk-primary-dark': theme.primaryDark,
    '--mk-on-primary': theme.onPrimary,
    '--mk-accent': theme.accent,
    // rgb triplets for rgba(var(--x), a) composition
    '--mk-primary-rgb': hexToRgb(theme.primary),
    '--mk-accent-rgb': hexToRgb(theme.accent),
    '--mk-text-rgb': hexToRgb(theme.text),
    '--mk-bg-rgb': hexToRgb(theme.bg),
    // derived shades used by the landing hero / CTA
    '--mk-primary-light': mix(theme.primary, '#FFFFFF', 0.45),
    '--mk-accent-deep': mix(theme.accent, '#000000', 0.78),
  } as React.CSSProperties;
}
